/**
 * Result screen toolbar with audio playback controls, review actions, and assessor notes.
 *
 * @module components/common/ResultScreen
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {makeStyles} from "@material-ui/core/styles";
import {connect} from "react-redux";
import Button from "@material-ui/core/Button";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import AddIcon from "@material-ui/icons/Add";
import {
    Modal,
    TextareaAutosize,
    Tooltip,
    IconButton,
} from "@material-ui/core";
import GetAppIcon from '@material-ui/icons/GetApp';
import {
    addAssignmentStatus,
    addAssessor,
    currentStatus,
} from "../../redux/sims/actions";
import {Alert} from "@material-ui/lab";
import StopIcon from "@material-ui/icons/Stop";
import {Redirect} from "react-router-dom";
import {useLocation} from "react-router-dom";
import DashboardPermissions from '../../permissions/dashboard';
import {permissionCheck} from '../../utils/permissionCheck';
import VisibilityIcon from "@material-ui/icons/Visibility";
import ResultScreenStyle from './ResultScreen';
import HelpRequestButton from "../helpdesk/HelpRequestButton";
import {audioListStop, setPlaylistType} from "../../redux/audio/action";

/**
 * Component for displaying the result screen of an application.
 * It provides functionality for managing assignment reviews, handling audio playback,
 * and navigating between different states within the result section.
 *
 * @component
 *
 * @param {Object} props - Properties passed down to the ResultScreen component.
 * @param {Object} props.userResult - The result object containing details about the user's assignment.
 * @param {Function} props.addAssignmentStatus - Function to update the status of an assignment (e.g., pending, reviewing).
 * @param {Function} props.takeSimFromAssignmentSection - Function to retrieve simulations associated with the assignment section.
 * @param {Function} props.addAssessor - Function to add or manage an assessor for the user/assignment.
 * @param {Function} props.showReviewerButtons - Function to toggle the visibility of reviewer action buttons on the screen.
 * @param {Boolean} props.allAudioProcessed - Indicates whether all audio files have been processed.
 * @param {Function} props.setPlaylistType - Function to set the type of audio playlist (e.g., call, agent, single).
 * @param {Function} props.audioListStop - Function to stop playback of the currently active audio playlist.
 * @param {Object} props.playList - Object containing information about the current playlist (e.g., type, status).
 * @param {string} props.playListType - String representing the type or category of the active playlist.
 */
const ResultScreen = ({
    userResult,
    addAssignmentStatus,
    takeSimFromAssignmentSection,
    addAssessor,
    showReviewerButtons,
    allAudioProcessed,
    setPlaylistType,
    audioListStop,
    playList,
    playListType,
}) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const useStyles = makeStyles((theme) => ResultScreenStyle(theme));
    const classes = useStyles();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const reviewer_come = urlParams.get("reviewer") ?? "false";
    const assignment_id = urlParams.get('assignment_id');
    const getModalStyle = () => {
        const top = 50;
        const left = 50;

        return {
            top: `${top}%`,
            left: `${left}%`,
            transform: `translate(-${top}%, -${left}%)`,
        };
    };

    const [openModal, setOpenModal] = React.useState(false);
    const [modalTitle, setModalTitle] = React.useState("");
    const [modalBody, setModalBody] = React.useState("");
    const [reviewButtonsDisable, setReviewButtonsDisable] = React.useState(false);
    const [showModalButton, setShowModalButton] = React.useState(false);
    const [redirectToDashboard, setRedirectToDashboard] = React.useState(false);
    const [showReviewerSubmitButton, setShowReviewerSubmitButton,] = React.useState(false);
    const [reviewerStatus, setReviewerStatus] = React.useState("");
    const [alertMessage, setAlertMessage] = React.useState("");
    const [openAlert, setOpenAlert] = React.useState(false);
    const [modalStyle] = React.useState(getModalStyle);
    const [audioPlaying, setAudioPlaying] = useState("");
    const reviewInputRef = React.useRef(null);
    const download = useRef(null);

    useEffect(
        () => {
            if (playList) {
                setAudioPlaying(playList.type);
            }
        },
        [playList]
    );

    useEffect(
        () => {
            setAudioPlaying(playListType ?? '');
        },
        [playListType]
    );

    // Stable ref so the once-on-mount DOM listener always calls the latest
    // callback version without going stale.
    const onPlaylistEndRef = React.useRef(null);
    const onPlaylistStopRef = React.useRef(null);

    useEffect(() => {
        onPlaylistEndRef.current = onPlaylistEnd;
        onPlaylistStopRef.current = onPlaylistStop;
    });

    useEffect(
        () => {
            const playlistEndProxy = (ev) => onPlaylistEndRef.current && onPlaylistEndRef.current(ev);
            const playlistStopProxy = (ev) => onPlaylistStopRef.current && onPlaylistStopRef.current(ev);
            document.addEventListener('playlist-end', playlistEndProxy);
            document.addEventListener('playlist-stop', playlistStopProxy);
            return () => {
                document.removeEventListener('playlist-end', playlistEndProxy);
                document.removeEventListener('playlist-stop', playlistStopProxy);
            };
        },
        []
    );

    /**
     * Callback triggered when the playlist finishes playing naturally.
     * Resets the toolbar button back to the play state.
     */
    const onPlaylistEnd = () => {
        setAudioPlaying('');
        handleStop();
    }

    /**
     * Callback triggered when the playlist is stopped/interrupted by user action
     * (e.g., clicking pause on an individual track while playlist is playing).
     * Resets the toolbar button back to the play state.
     */
    const onPlaylistStop = () => {
        setAudioPlaying('');
        handleStop();
    }

    /**
     * Function that initializes and displays a modal to confirm
     * if the user wants to send a sim for review.
     *
     * The modal includes a customizable title and body, informing
     * the user about the implications of sending the sim for review.
     * It also triggers the display of a button to confirm the action.
     * Once activated, the modal remains open until further user action.
     */
    const sendForReview = () => {
        setModalTitle("Are You Sure");
        setModalBody(
            "Are you ready to send this sim for review? Once you click send, the sim will be locked until your trainer reviews your effort."
        );
        setShowModalButton(true);
        setOpenModal(true);
    };

    /**
     * Handles the process of confirming and initiating the "Send for Review" action for an assignment.
     *
     * This function performs the following:
     * - Prepares the payload with assignment ID, response ID, and status set to "PENDING REVIEW".
     * - Calls the function to update the assignment status to "PENDING REVIEW".
     * - Sets an alert message to inform the user about the successful operation.
     * - Displays the alert temporarily and prepares to redirect the user to the dashboard.
     * - Closes the modal associated with the action.
     * - Updates the local `assignment_status` property of the user result object to "PENDING REVIEW".
     */
    const confirmSendForReview = () => {
        const payload = {
            _id: userResult?.assignment_id ?? assignment_id,
            status: "PENDING REVIEW",
            response_id: userResult?._id,
        };

        // update assignment status to PENDING REVIEW
        addAssignmentStatus(payload);
        setAlertMessage("Assignment is send for review successfully!");
        setOpenAlert(true);
        setTimeout(
            () => {
                setOpenAlert(false);
                setRedirectToDashboard(true);
            },
            3000
        );
        setOpenModal(false);
        userResult.assignment_status = "PENDING REVIEW";
    };

    /**
     * A function to handle the closing of a modal.
     *
     * This function is used to set the state of the modal to closed
     * by updating the related state variable. It sets the modal's
     * visibility status to false when invoked.
     */
    const handleModalClose = () => {
        setOpenModal(false);
    };

    /**
     * Function to handle the logic for playing call audio.
     *
     * This method sets the playlist type to 'call', initiating the appropriate
     * playlist configuration or behavior associated with playing call-related audio.
     */
    const handlePlayCallAudio = () => {
        setPlaylistType('call');
    };

    /**
     * This variable is a function that sets the playlist type to 'agent'.
     * It is used to manage the playback of audio associated with an agent.
     */
    const handlePlayAgentAudio = () => {
        setPlaylistType('agent');
    };

    /**
     * This function is triggered to handle the stop event for an audio playlist.
     * It sets the playlist type to 'single' and invokes the audioListStop function to stop the audio.
     */
    const handleStop = () => {
        setPlaylistType('single');
        audioListStop();
    };

    /**
     * Opens the "Pass Review" modal and updates the associated state variables.
     *
     * This function performs the following operations:
     * - Hides the modal button by setting `setShowModalButton` to false.
     * - Updates the reviewer's status to "PASS" using `setReviewerStatus`.
     * - Sets the modal title to "Marked the assignment as Passed" using `setModalTitle`.
     * - Clears the modal body content using `setModalBody`.
     * - Displays the reviewer submit button by setting `setShowReviewerSubmitButton` to true.
     * - Activates the modal display by calling `setOpenModal` with true.
     */
    const openPassReviewModal = () => {
        setShowModalButton(false);
        setReviewerStatus("PASS");
        setModalTitle("Marked the assignment as Passed");
        setModalBody("");
        setShowReviewerSubmitButton(true);
        setOpenModal(true);
    };

    /**
     * Function to open a modal indicating the failure of a review.
     * This function performs the following actions:
     * - Disables the display of the modal button.
     * - Sets the reviewer's status to "NO PASS".
     * - Updates the modal title to indicate the failure status.
     * - Clears the modal body content.
     * - Displays the reviewer's submit button.
     * - Opens the modal to show these updated details.
     */
    const openFailedReviewModal = () => {
        setShowModalButton(false);
        setReviewerStatus("NO PASS");
        setModalTitle("Marked the assignment as No Pass");
        setModalBody("");
        setShowReviewerSubmitButton(true);
        setOpenModal(true);
    };

    /**
     * Handles the submission of a review assignment by updating its status, adding comments, and triggering UI changes.
     *
     * @function submitReviewAssignment
     *
     * The function performs the following actions:
     * - Gathers the review parameters, including assignment ID, review status, review comments, and response ID.
     * - Calls the `addAssignmentStatus` function to save the review details.
     * - Closes the review modal window.
     * - Disables the review buttons to prevent further interaction after submission.
     * - Displays a success alert message confirming the review was completed.
     * - Hides the success alert and redirects the user to the dashboard after a timeout of 3 seconds.
     */
    const submitReviewAssignment = () => {
        const reviewParams = {
            _id: userResult?.assignment_id ?? assignment_id,
            status: reviewerStatus,
            review_comments: reviewInputRef.current.value,
            response_id: userResult._id,
        };
        addAssignmentStatus(reviewParams);
        setOpenModal(false);
        setReviewButtonsDisable(true);
        setAlertMessage("Assignment Reviewed Successfully!");
        setOpenAlert(true);
        setTimeout(
            () => {
                setOpenAlert(false);
                setRedirectToDashboard(true);
            },
            3000
        );
    };

    /**
     * Conditionally renders review-related buttons based on user role, task state, and other assignment statuses.
     * The function performs checks to determine if specific action buttons, such as "PASS", "NO PASS",
     * or "SEND FOR REVIEW", should be displayed. These buttons enable users to pass, not pass, or send
     * a review request for an assignment or task, depending on several contextual conditions.
     *
     * Decision points include:
     * - User role and if they are eligible to perform review actions.
     * - Whether the task has specific flags (e.g., `send_for_review`) and state consistency conditions.
     * - The processing status of underlying audio levels (e.g., `allAudioProcessed`).
     *
     * Rendering logic includes:
     * 1. Showing "PASS" and "NO PASS" buttons when user roles and review criteria align.
     * 2. Displaying a "SEND FOR REVIEW" button with tooltip guidance when certain conditions are met.
     *
     * Returns:
     * - JSX elements containing the appropriate button(s) when conditions are satisfied.
     * - Otherwise, returns `null` to render nothing.
     *
     * Dependencies:
     * - Relies on user data (e.g., `user.role`, `userResult`) and state flags (e.g., `showReviewerButtons`).
     * - Uses helper methods for actions such as opening modals (`openPassReviewModal`, `openFailedReviewModal`)
     *   and sending tasks for review (`sendForReview`).
     *
     * Designed for dynamic user interaction in task review workflows.
     */
    const showReviewButtons = () => {
        if (
            (user.role).toLowerCase() !== 'agent' &&
            showReviewerButtons &&
            (
                (userResult?.sim_level_id?.toString() === userResult?.pass_fail_details?.sim_level_id?.toString()) ||
                userResult?.send_for_review
            )
        ) {
            return (
                <>
                    <Button
                        variant="contained"
                        className={classes.startBtn}
                        disableElevation
                        disabled={reviewButtonsDisable}
                        color="primary"
                        size="small"
                        onClick={openPassReviewModal}
                    >
                        PASS
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.startBtn}
                        disableElevation
                        disabled={reviewButtonsDisable}
                        color="primary"
                        size="small"
                        onClick={openFailedReviewModal}
                    >
                        NO PASS
                    </Button>
                </>
            );
        }

        return null;
    };

    /**
     * Determines whether the "Send for Review" button should be displayed.
     *
     * The function evaluates multiple conditions to decide the visibility and availability
     * of the "Send for Review" button. These conditions include:
     *
     * - The presence and status of a reviewer (`reviewer_come`).
     * - The assignment status (`assignment_status`), ensuring it is neither "PASS" nor "NO PASS".
     * - Validation of the `isPassFailLevel`, which depends on:
     *   - The `pass_fail_details` object in `userResult`, validating against `sim_level_id`.
     *   - Whether `assignment_status` is "COMPLETED" when `pass_fail_details` is absent.
     * - Completion of required levels through the `takeSimFromAssignmentSection` flag.
     * - The `send_for_review` property in `userResult`, which must be true.
     * - Audio-processing status across all levels, controlled by `allAudioProcessed`.
     *
     * When all conditions are met, the function renders a button within a `Tooltip` component
     * with various states and messages:
     *
     * - If all audio levels are processed and the assignment is not already under review,
     *   a clickable "Send for Review" button is rendered.
     * - If all levels are not processed, or the assignment is "PENDING REVIEW",
     *   the button remains disabled with an explanatory tooltip.
     *
     * If conditions are not met, the function returns `null`, and no button is displayed.
     */
    const showSendForReviewButton = () => {
        /**
         * Determines if the current result meets the pass/fail level criteria.
         *
         * The value of `isPassFailLevel` is determined based on the following conditions:
         * - If `userResult.pass_fail_details` is defined:
         *   - Compares the stringified `sim_level_id` in `userResult` with `sim_level_id` in `pass_fail_details`.
         * - If `userResult.pass_fail_details` is not defined:
         *   - Checks if `assignment_status` in `userResult` equals 'COMPLETED'.
         *
         * This variable prevents type mismatches by ensuring comparisons are performed
         * using stringified values where applicable.
         */
        const isPassFailLevel = userResult?.pass_fail_details ?
            (userResult?.sim_level_id?.toString() === userResult?.pass_fail_details?.sim_level_id?.toString()) :
            (userResult?.assignment_status === 'COMPLETED')

        if (
            (!reviewer_come || (reviewer_come === "false")) &&
            !["PASS", "NO PASS"].includes(userResult?.assignment_status) &&
            takeSimFromAssignmentSection &&
            userResult?.send_for_review &&
            !userResult?.assignment_passed &&
            isPassFailLevel
        ) {
            return (
                <Tooltip
                    title={
                        allAudioProcessed ? (
                                (userResult?.assignment_status === "PENDING REVIEW") ?
                                    "Assignment is already in under review!" :
                                    (
                                        userResult?.send_for_review ?
                                            "Want to send for review?" :
                                            "Enable only if all the levels completed!"
                                    )
                            ) :
                            "Button should be enabled only after processing the audios in all levels which are completed"
                    }
                    placement="top"
                >
                    <span>
                        <Button
                            variant="contained"
                            className={classes.startBtn}
                            disableElevation
                            color="primary"
                            size="small"
                            disabled={
                                allAudioProcessed ? (
                                    (!(userResult?.send_for_review && userResult?.assignment_status !== "PENDING REVIEW"))
                                ) : true
                            }
                            onClick={sendForReview}
                        >
                            SEND FOR REVIEW
                        </Button>
                    </span>
                </Tooltip>
            );
        }

        return null;
    }

    /**
     * Determines and renders the UI for displaying or adding assessor notes
     * based on the user's permissions and current user result state.
     *
     * This function evaluates the permissions of the user to check whether:
     * - The user can view assessor notes.
     * - The user can create or update assessor notes.
     *
     * Depending on the permissions, it renders a button with appropriate
     * labels and functionality:
     * - "View Assessor Notes" button if the user has only view permissions.
     * - "Add Assessor Notes" button if the user has create or update permissions.
     * - Renders nothing if the user has no relevant permissions.
     *
     * The button is disabled if the `userResult` object is empty.
     *
     * @function
     * @returns {ReactElement|null} A tooltip-wrapped button component for viewing
     * or adding assessor notes, or `null` if permissions are insufficient.
     */
    const showAssessorNotes = () => {
        if (
            !permissionCheck(
                DashboardPermissions.assessor_notes.update.task,
                DashboardPermissions.assessor_notes.update.type
            ) &&
            !permissionCheck(
                DashboardPermissions.assessor_notes.create.task,
                DashboardPermissions.assessor_notes.create.type
            ) &&
            permissionCheck(
                DashboardPermissions.assessor_notes.view.task,
                DashboardPermissions.assessor_notes.view.type
            )
        ) {
            return (<Tooltip title='View Assessor Notes' placement="bottom">
                    <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        className={classes.assessor}
                        onClick={() => addAssessor(true)}
                        disabled={Object.keys(userResult).length === 0}
                    >
                        <VisibilityIcon style={{marginRight: "5px"}}/>
                        ASSESSOR NOTES
                    </Button>
                </Tooltip>
            );
        }

        if (
            permissionCheck(
                DashboardPermissions.assessor_notes.update.task,
                DashboardPermissions.assessor_notes.update.type
            ) ||
            permissionCheck(
                DashboardPermissions.assessor_notes.create.task,
                DashboardPermissions.assessor_notes.create.type
            )
        ) {
            return (
                <Tooltip title='Add Assessor Notes' placement="bottom">
                    <span>
                        <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            className={classes.assessor}
                            disabled={Object.keys(userResult).length === 0}
                            onClick={() => addAssessor(true)}
                        >
                            <AddIcon style={{marginRight: "5px"}}/>
                            ASSESSOR NOTES
                        </Button>
                    </span>
                </Tooltip>)
        }

        return null;
    };

    return (
        <div style={{textAlign: "right", width: "70%"}}>
            {redirectToDashboard && <Redirect to="/dashboard"/>}
            <Modal
                open={openModal}
                onClose={handleModalClose}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
            >
                <div style={modalStyle} className={classes.modalPaper}>
                    <h2>{modalTitle}</h2>
                    <p>{modalBody}</p>
                    {showModalButton ? (
                        <>
                            <Button
                                onClick={confirmSendForReview}
                                color="primary"
                                variant="contained"
                                style={{float: "right", margin: "0.5rem 1rem 0.5rem 0rem"}}
                            >
                                Continue
                            </Button>
                            <Button
                                onClick={handleModalClose}
                                variant="contained"
                                style={{float: "right", margin: "0.5rem 1rem 0.5rem 0rem"}}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : null}
                    {showReviewerSubmitButton ? (
                        <>
                            <TextareaAutosize
                                style={{width: "100%", height: "5rem", padding: ".4rem"}}
                                ref={reviewInputRef}
                                aria-label="minimum height"
                                maxRows={10}
                                placeholder="Write Message to Learner..."
                                data-enable-grammarly="false"
                            />
                            <br/>
                            <Button
                                onClick={submitReviewAssignment}
                                color="primary"
                                variant="contained"
                                style={{float: "right", margin: "0.5rem 0rem 0rem 0rem"}}
                            >
                                Submit
                            </Button>
                        </>
                    ) : null
                    }
                </div>
            </Modal>

            <>
                {showReviewButtons()}
                {showSendForReviewButton()}
            </>

            <HelpRequestButton simData={{sim_id: userResult?.level?.sim_id ?? '', level: userResult?.level ?? null, step: -1}}
                               classes={classes}
            />

            {showAssessorNotes()}

            {
                (user.role).toLowerCase() !== 'agent' &&
                <Tooltip title="Download recorded video">
                    <IconButton size="small" color="primary" className={classes.downloadBtn}>
                        <a ref={download} style={{display: "none"}} href="#df">
                            <GetAppIcon/>
                        </a>
                    </IconButton>
                </Tooltip>
            }

            {audioPlaying === "call" ? (
                <Button
                    onClick={handleStop}
                    variant="contained"
                    size="small"
                    disableElevation
                    className={classes.audioButtons}
                >
                    <StopIcon style={{marginRight: "5px"}}/>
                    Stop
                </Button>
            ) : (
                <Button
                    onClick={handlePlayCallAudio}
                    variant="contained"
                    size="small"
                    disableElevation
                    className={classes.audioButtons}
                >
                    <PlayArrowIcon style={{marginRight: "5px"}}/>
                    CALL AUDIO
                </Button>
            )}
            {audioPlaying === "agent" ? (
                <Button
                    onClick={handleStop}
                    variant="contained"
                    size="small"
                    disableElevation
                    className={classes.audioButtons}
                    style={{marginLeft: "0px"}}
                >
                    <StopIcon style={{marginRight: "5px"}}/>
                    Stop
                </Button>
            ) : (
                <Button
                    onClick={handlePlayAgentAudio}
                    variant="contained"
                    size="small"
                    disableElevation
                    className={classes.audioButtons}
                    style={{marginLeft: "0px"}}
                >
                    <PlayArrowIcon style={{marginRight: "5px"}}/>
                    AGENT AUDIO
                </Button>
            )}
            {openAlert ? (
                <Alert className={classes.alert} variant="filled" severity="success">
                    {alertMessage}
                </Alert>
            ) : null}
        </div>
    );
};

/**
 * PropTypes for the ResultScreen component.
 *
 * Defines the type and requirements for the props
 * that can be passed to the ResultScreen component.
 *
 * @property {string} title - The title text to be displayed on the result screen. Required.
 * @property {string} description - A detailed description of the result. Optional.
 * @property {boolean} success - Indicates whether the result is a success. Required.
 * @property {function} onRetry - Callback function to be invoked when retrying. Optional.
 * @property {ReactNode} children - Elements or components to be rendered inside the ResultScreen. Optional.
 */
ResultScreen.propTypes = {
    userResult: PropTypes.object,
    addAssignmentStatus: PropTypes.func,
    addAssessor: PropTypes.func,
    currentStatus: PropTypes.func,
    takeSimFromAssignmentSection: PropTypes.bool,
    showReviewerButtons: PropTypes.bool,
    allAudioProcessed: PropTypes.bool,
    setPlaylistType: PropTypes.func,
    audioListStop: PropTypes.func,
    playList: PropTypes.object,
    playListType: PropTypes.string,
    playlist: PropTypes.object,
    setPlaylist: PropTypes.func,
};

/**
 * Maps the state from the Redux store to the component's props.
 *
 * @param {Object} state - The current state of the Redux store.
 * @returns {Object} An object containing the mapped state properties.
 * @property {Object} userResult - The `userResult` property from the `sim` state.
 * @property {boolean} takeSimFromAssignmentSection - The `takeSimFromAssignmentSection` property from the `sim` state.
 * @property {boolean} showReviewerButtons - A boolean indicating the visibility of reviewer buttons, derived from `sim.showReviewerButtons`.
 * @property {boolean} allAudioProcessed - The `allAudioProcessed` property from the `sim` state.
 * @property {Object} playlist - The entire `audio` state object.
 * @property {string} playListType - The `type` property of the `audio` state.
 */
const mapStateToProps = (state) => ({
    userResult: state.sim.userResult,
    takeSimFromAssignmentSection: state.sim.takeSimFromAssignmentSection,
    showReviewerButtons: !!state.sim.showReviewerButtons,
    allAudioProcessed: state.sim.allAudioProcessed,
    playlist: state.audio,
    playListType: state.audio.type,
});

/**
 * Object mapping dispatchable action creators to props for a React-Redux connected component.
 * This map allows the component to invoke Redux actions directly as props.
 *
 * Properties:
 * - `addAssignmentStatus` - Action creator for managing assignment status updates.
 * - `addAssessor` - Action creator for adding an assessor to the state.
 * - `currentStatus` - Action creator for retrieving or setting the current status.
 * - `setPlaylistType` - Action creator for setting the type of playlist.
 * - `audioListStop` - Action creator for stopping the audio list playback.
 */
const mapDispatchToProps = {
    addAssignmentStatus,
    addAssessor,
    currentStatus,
    setPlaylistType,
    audioListStop,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultScreen);
